import type { RealtimeToken } from '@soniox/client'
import type { SttProvider } from '@ai-meeting/shared'
import { fetchSonioxToken, type SonioxTokenResponse } from './soniox'
import { getCurrentUserId, getCurrentUserName } from './session'
import { ensureBacklogUserId } from './backlogAccount'
import type { TeamsAudioFrame, TeamsAudioParticipant } from './teams-gateway-audio'
import { proxyEventToTokens, type ProxySttEvent } from '@/utils/proxySttEventProcessor'
import { normalizeSttConfigContext, type SttConfigContext } from '@/utils/sttConfigContext'
import { isMemoPaneEvent } from '@/utils/applyMemoPane'
import { sanitizeFeatureUpdate, type RealtimeFeatures } from '@/utils/realtimeFeatures'
import type { AssistModel } from '@/pages/Meetings/components/Copilot/assistModel'
import type { AssistRoute } from '@/pages/Meetings/components/Copilot/assist.types'
import { ParticipantTranscriptDisplayQueue } from '@/utils/participantTranscriptDisplayQueue'

export interface ParticipantAiTokenUpdate {
  finalTokens: RealtimeToken[]
  partialTokens: RealtimeToken[]
  batchTokens: RealtimeToken[]
  clearPartial?: boolean
  partialStatus?: RealtimeToken['translation_status']
}

/**
 * A `summarize.*` / `meeting-minutes.*` push from the AI service.
 *
 * These describe the MEETING, not this participant: the server runs one set of summarize workers
 * per meetingId and broadcasts each result to every participant socket, so the same payload arrives
 * once per socket. Every one is a full-replace snapshot, so applying them in arrival order is
 * correct however many duplicates land.
 */
export interface ParticipantAiInsightsEvent {
  event: string
  /**
   * The frame's body, shape decided by `event`.
   *
   * Deliberately `unknown` rather than a union of the shapes it can be. It used to name
   * `PreviousAnalysis`, which stopped being true when the memo was split into three frames that each
   * carry one pane — and a union listing every partial shape would have to be edited every time a
   * service that is not ours adds a field. The readers narrow it: `applyMemoPane` for the memo
   * frames, an explicit cast for meeting minutes.
   */
  data?: unknown
  code?: string
  message?: string
  request_id?: string
}

export type ParticipantAiStreamEvent = ProxySttEvent

/**
 * Context handed to this socket's `config` frame.
 *
 * Deliberately the same type as the realtime STT path: both frames describe the
 * same `config.context` field, so an alias (rather than a second declaration)
 * keeps the two from drifting apart.
 */
export type ParticipantAiStreamContext = SttConfigContext

interface ParticipantAiStreamCoordinatorOptions {
  meetingId: string
  targetLang: string
  sourceLang?: string
  mode?: 'one_way' | 'two_way'
  sttProvider?: SttProvider
  /**
   * Backlog project this meeting was created against, or null when it named none.
   *
   * Carried on every participant's config rather than once per meeting,
   * because in native mode each speaker opens their own socket and the server
   * reads the project off the frame it is given.
   */
  projectKey?: string | null
  context?: ParticipantAiStreamContext | null
  pendingFrameCapacity?: number
  fetchToken?: () => Promise<SonioxTokenResponse>
  createWebSocket?: (url: string) => WebSocket
  onEvent?: (participant: TeamsAudioParticipant, event: ParticipantAiStreamEvent) => void
  onTokens: (participant: TeamsAudioParticipant, update: ParticipantAiTokenUpdate) => void
  /** Meeting-level summary / minutes pushes. Same payload arrives once per participant socket. */
  onInsights?: (event: ParticipantAiInsightsEvent) => void
  /**
   * End-of-meeting `cost.summary`, per participant socket.
   *
   * Unlike the summary, this is NOT the same number on every socket: each one reports what its own
   * STT + translation cost. The caller must therefore ADD them up — taking the last one (which is
   * what a single-socket flow does) reports one participant's share as the whole meeting's bill.
   */
  onCost?: (participant: TeamsAudioParticipant, currency: string, totalCostUsd: number) => void
  /**
   * Server verdict on a live `language.update`, once per socket that was told about it.
   *
   * Every open socket answers separately, so the caller sees N results for one request — see
   * `ParticipantAiStreamCoordinator.setTargetLang` for how many were asked.
   */
  onLanguageUpdate?: (result: ParticipantAiLanguageUpdateResult) => void
  onError?: (error: Error, participant: TeamsAudioParticipant) => void
  /**
   * How far the meeting has run, read at the moment this socket sends its config.
   *
   * A callback, not a number, and the distinction matters: in native mode each
   * participant's socket opens when that person FIRST SPEAKS, so a value captured
   * once would stamp every socket with the first speaker's elapsed time. The
   * server uses it to place a new timeline anchor where the client already is;
   * without it the anchor lands wherever the first socket happened to open, which
   * is the first word spoken rather than the start of the meeting.
   */
  getMeetingElapsedMs?: () => number
  /**
   * The server's verdict on which clock the transcript timestamps are measured from.
   *
   * `resumed` true means the server re-joined a timeline it already had — its
   * record is older and more trustworthy, so the caller should move its own origin
   * to `Date.now() - seedMs`. False means the server just set the timeline from
   * what this client reported, and the caller keeps the origin it has.
   */
  onTimelineAnchor?: (info: { seedMs: number; resumed: boolean }) => void
  /** Pane switches for this socket's `config`. Omitted when the caller has none. */
  getRealtimeFeatures?: () => RealtimeFeatures
  /**
   * Which assistant answers Copilot, for this socket's `config`.
   *
   * One answer for the whole meeting, not one per participant: the reader is
   * looking at a single Copilot panel no matter how many sockets are feeding
   * it, so every stream is told the same thing.
   */
  getAssistModel?: () => AssistModel
  /** `assist.model.status` from this socket. */
  onAssistModelStatus?: (event: unknown) => void
  /** `assist.model.update.error` from this socket. Non-fatal. */
  onAssistModelError?: (code: 'bad_request' | 'not_supported') => void
  /** Which kinds of question the Copilot answers, for this socket's `config`. */
  getAssistRoutes?: () => AssistRoute[]
  /** `assist.routes.status` from this socket. */
  onAssistRoutesStatus?: (event: unknown) => void
  /** `assist.routes.update.error` from this socket. Non-fatal. */
  onAssistRoutesError?: (code: 'bad_request' | 'not_supported') => void
  /** `ready` on this participant's socket. */
  onRealtimeFeaturesReady?: (event: { features?: unknown }) => void
  /** `features.updated` for the socket that sent `features.update`. */
  onRealtimeFeaturesUpdated?: (event: { features?: unknown }) => void
  /** `features.update.error`. The socket stays open. */
  onRealtimeFeaturesError?: (code: 'bad_request' | 'not_supported') => void
}

/** One socket's answer to a live translation-language change. */
export interface ParticipantAiLanguageUpdateResult {
  participant: TeamsAudioParticipant
  ok: boolean
  /** Language the server says it applied; only set when `ok`. */
  targetLang?: string
  /** Server-supplied reason; only set when the update was rejected. */
  message?: string
}

const OPEN_STATE = 1
const DEFAULT_PENDING_FRAME_CAPACITY = 200
const STOP_TIMEOUT_MS = 2_000
/**
 * Unreported media time worth spending an `audio.gap` message on.
 *
 * Throttles how many messages go out, and nothing else: time below it stays OWED rather than being
 * discarded, because the accounted position does not move for silence that was not reported (see
 * `sendAccounted`). The previous frame-to-frame measurement had no such property — anything under
 * 1.5s was dropped outright and left the timeline for good, which is one of the ways the Input
 * column fell behind the meeting clock (BRAI-402).
 *
 * The old threshold was set high to keep packet jitter from reporting a gap. It never needed to be:
 * the gateway stamps each frame with Graph's MEDIA timestamp rather than an arrival time, and
 * jitter does not move that.
 */
const AUDIO_GAP_MIN_MS = 200

/**
 * Bytes of the audio this client sends, per millisecond: 16 kHz, mono, signed 16-bit.
 *
 * Must stay equal to the server's own `bytes_per_ms`, which it derives from the `sampleRate`
 * declared in `config`. The two are counting the same stream, and any disagreement IS drift.
 */
const AUDIO_BYTES_PER_MS = 32

function toProxySttProvider(provider: SttProvider): string {
  return provider === 'azure' ? 'azure_speech_recognizer' : provider
}

function tokenizedWebSocketUrl(wsUrl: string, token: string): string {
  const url = new URL(wsUrl)
  url.searchParams.set('token', token)
  return url.toString()
}

/**
 * Kept even though the server now labels this socket's speech with the same participant (see the
 * `participant` field on `config`): the rendered timeline keys partials and grouping off
 * `providerParticipantId`, and `speaker_name` is what it displays. The server-side label exists for
 * the MERGED transcript the summarizers read, which the client never sees.
 */
function applyTeamsIdentity(
  participant: TeamsAudioParticipant,
  tokens: RealtimeToken[]
): RealtimeToken[] {
  return tokens.map((token) => ({
    ...token,
    speaker: participant.providerParticipantId,
    speaker_name: participant.displayName,
  }))
}

/**
 * Events that describe the meeting rather than this participant's audio (see the interface).
 *
 * The three per-pane frames are matched by exact name through `isMemoPaneEvent`, not by prefix:
 * `main-content.updated` and `main-content.update.error` already exist and answer one client's own
 * edit, so a `main-content.` prefix would quietly reroute them here. Missing the three entirely is
 * the worse failure though — anything this returns false for is handled as this participant's
 * audio, so the native path would show an empty live memo for the whole meeting.
 */
function isInsightsEvent(event: string | undefined): boolean {
  return (
    event !== undefined &&
    (event.startsWith('summarize.') ||
      event.startsWith('meeting-minutes.') ||
      isMemoPaneEvent(event))
  )
}

/**
 * Rewrites one id into this socket's namespace, leaving absent ids absent.
 *
 * @param scope Prefix identifying the socket the id came from.
 */
function scopeId(scope: string, id: string | number | undefined): string | undefined {
  if (id === undefined || id === null || id === '') return undefined
  return `${scope}:${id}`
}

function scopeIds(
  scope: string,
  ids: Array<string | number> | undefined
): string[] | undefined {
  return ids?.map((id) => `${scope}:${id}`)
}

/**
 * Namespaces every transcript id in an event to the socket it arrived on.
 *
 * Each participant has their OWN AI WebSocket, and each of those numbers its
 * `segment_id`/`batch_id` from scratch — so two people talking produce two
 * `segment-1`s. `upsertTurn` treats those ids as globally unique and merges on
 * them, which collapsed every speaker into one timeline card and left only the
 * last one's batch translation visible. The backend-relayed path already scopes
 * ids per stream (`stream-1:batch-1`); this does the same for the browser-direct
 * path so the two can never collide.
 */
function scopeEventIds(scope: string, event: ProxySttEvent): ProxySttEvent {
  return {
    ...event,
    segment_id: scopeId(scope, event.segment_id ?? undefined) ?? event.segment_id,
    source_segment_id:
      scopeId(scope, event.source_segment_id ?? undefined) ?? event.source_segment_id,
    source_segment_ids: scopeIds(scope, event.source_segment_ids),
    batch_id: scopeId(scope, event.batch_id),
    batchId: scopeId(scope, event.batchId),
    source_turns: event.source_turns?.map((turn) => ({
      ...turn,
      source_segment_ids: scopeIds(scope, turn.source_segment_ids),
    })),
    translation_turns: event.translation_turns?.map((turn) => ({
      ...turn,
      batch_id: scopeId(scope, turn.batch_id),
      batchId: scopeId(scope, turn.batchId),
    })),
  }
}

function applyTeamsIdentityToEvent(
  participant: TeamsAudioParticipant,
  event: ProxySttEvent
): ProxySttEvent {
  const speaker = participant.providerParticipantId
  // Scope by the join id, not the participant id: a participant who leaves and
  // rejoins gets a fresh socket with a fresh id space, and reusing the earlier
  // scope would make the new socket's `segment-1` collide with the old one's.
  const scoped = scopeEventIds(participant.providerSessionId, event)
  return {
    ...scoped,
    speaker,
    speaker_name: participant.displayName,
    tokens: scoped.tokens?.map((token) => ({ ...token, speaker })),
    source_turns: scoped.source_turns?.map((turn) => ({ ...turn, speaker })),
    translation_turns: scoped.translation_turns?.map((turn) => ({ ...turn, speaker })),
  }
}

class ParticipantAiStreamClient {
  private readonly participant: TeamsAudioParticipant
  private readonly options: ParticipantAiStreamCoordinatorOptions
  private readonly displayQueue: ParticipantTranscriptDisplayQueue<
    TeamsAudioParticipant,
    ProxySttEvent,
    ParticipantAiTokenUpdate
  >
  private readonly pendingFrameCapacity: number
  private readonly pendingFrames: Array<{ pcm16: Uint8Array; timestampMs: number }> = []
  private socket: WebSocket | null = null
  private connectPromise: Promise<void> | null = null
  private ready = false
  private closed = false
  /**
   * The reader's Backlog account id, read once when this socket opens.
   *
   * Held per socket although the answer is the same for all of them: native
   * mode runs one of these per participant, and the lookup behind it is cached
   * module-wide, so the first socket to open pays for the whole meeting.
   */
  private backlogUserId: number | null = null
  private closeResolver: (() => void) | null = null
  /**
   * Media timestamp of the first frame sent since the last accounting reset, or `null` before it.
   *
   * With `sentAudioMs` and `reportedGapMs` this is the whole of what the server has been told about
   * where this participant sits in media time. Resetting all three is how a span the SERVER
   * accounts for costs nothing here: it adds the app's own pause to its timeline itself, so
   * reporting that same silence as a gap shifted every later timestamp by the pause TWICE
   * (BRAI-402).
   */
  private mediaBaseMs: number | null = null
  /** Audio put on the wire since `mediaBaseMs`, in ms, derived from the bytes actually sent. */
  private sentAudioMs = 0
  /** Silence reported since `mediaBaseMs`, in ms, as the server was told it. */
  private reportedGapMs = 0
  /** Latest meeting roster (`providerParticipantId -> displayName`), re-sent whenever it grows. */
  private roster: Record<string, string> = {}
  /**
   * Language this socket is translating into. Kept here rather than read from `options` because the
   * user can change it mid-meeting, and this socket may not exist yet when they do.
   */
  private targetLang: string
  constructor(
    participant: TeamsAudioParticipant,
    options: ParticipantAiStreamCoordinatorOptions,
    displayQueue: ParticipantTranscriptDisplayQueue<
      TeamsAudioParticipant,
      ProxySttEvent,
      ParticipantAiTokenUpdate
    >
  ) {
    this.participant = participant
    this.options = options
    this.displayQueue = displayQueue
    this.targetLang = options.targetLang
    this.pendingFrameCapacity = options.pendingFrameCapacity ?? DEFAULT_PENDING_FRAME_CAPACITY
  }

  /**
   * Hand the server the meeting's roster (`speaker.map`), now or as soon as this socket is ready.
   *
   * Every socket gets the WHOLE roster, not just its own participant: the server merges each
   * socket's map into one per-meeting roster, so whichever socket connects first can already name
   * everyone the gateway has seen — including participants whose own socket does not exist yet
   * because they have not spoken (Teams only streams unmixed audio for active speakers).
   *
   * Names only, never ids-as-names, and no local filtering of the gateway's "Unknown speaker"
   * placeholder: the server owns that rule (`attribution_guard.clean_roster`) and applying it in two
   * places is how the two drift apart.
   */
  setRoster(roster: Record<string, string>): void {
    this.roster = roster
    if (this.ready) this.sendSpeakerMap()
  }

  private sendSpeakerMap(): void {
    if (this.socket?.readyState !== OPEN_STATE) return
    if (Object.keys(this.roster).length === 0) return
    this.socket.send(JSON.stringify({ event: 'speaker.map', mapping: this.roster }))
  }

  /**
   * Switch the language this socket translates into, mid-session.
   *
   * The language is remembered even when nothing can be sent yet: a socket still opening (or not
   * opened at all) carries the new value in its own `config`, so the change survives regardless of
   * when this participant first speaks.
   *
   * @param targetLang Language code to translate into from here on.
   * @returns `true` when a `language.update` went out and an acknowledgment is therefore coming.
   */
  setTargetLang(targetLang: string): boolean {
    this.targetLang = targetLang
    if (this.closed || !this.ready || this.socket?.readyState !== OPEN_STATE) return false
    this.socket.send(
      JSON.stringify({
        event: 'language.update',
        translation: {
          type: this.options.mode ?? 'one_way',
          sourceLang: this.options.sourceLang,
          targetLang,
        },
      })
    )
    return true
  }

  connect(): Promise<void> {
    if (this.connectPromise) return this.connectPromise
    this.connectPromise = this.open()
    return this.connectPromise
  }

  /**
   * Tell the server this participant's audio is deliberately stopping, or coming back.
   *
   * The same `pause`/`resume` protocol the Share Tab recording uses — both flows talk to the same
   * AI service over the same socket. On `pause` the server finalizes what it has, so the paused
   * span costs nothing instead of billing silence.
   *
   * Frames buffered while the socket was still opening are dropped rather than carried across:
   * the ready handler flushes them unconditionally, which would put audio on the wire in the
   * middle of a pause.
   *
   * The media accounting is reset along with them, so the first frame after `resume` reports no
   * silence at all. The server already adds the paused span to its own timeline when it sees
   * `resume`; reporting it here as well shifted everything after the pause by twice its length —
   * a 5-minute pause moved the rest of the transcript by 10 minutes (BRAI-402).
   */
  setPaused(paused: boolean): void {
    if (this.closed) return
    if (paused) {
      this.pendingFrames.length = 0
      this.resetMediaAccounting()
    }
    if (this.socket?.readyState !== OPEN_STATE) return
    this.socket.send(JSON.stringify({ event: paused ? 'pause' : 'resume' }))
  }

  writeAudio(frame: TeamsAudioFrame): void {
    if (this.closed) return
    if (this.ready && this.socket?.readyState === OPEN_STATE) {
      this.sendAccounted(frame.pcm16, frame.timestampMs)
      return
    }
    if (this.pendingFrames.length >= this.pendingFrameCapacity) this.pendingFrames.shift()
    this.pendingFrames.push({ pcm16: frame.pcm16.slice(), timestampMs: frame.timestampMs })
  }

  /**
   * Send one frame, keeping the server's picture of this participant's media time exact.
   *
   * Teams delivers unmixed audio for ACTIVE SPEAKERS only, so a participant's stream simply stops
   * while they are not talking. The server derives every transcript timestamp from how much audio
   * it has received, so those holes collapse out of the timeline: measured over a 4-minute meeting,
   * the three participant sockets ran 218s / 189s / 57s behind the wall clock, which is why their
   * transcripts could not be merged in order. The gateway stamps each frame with a real media
   * timestamp, so the hole is visible here — and only here.
   *
   * Measured as the difference between two ABSOLUTE quantities — where this frame sits on the media
   * clock, and how far the server has been told about — never as the distance between two
   * consecutive frames. Frame-to-frame was wrong twice over: it measured from the previous frame's
   * START, so every gap over-reported by that frame's own duration and the error grew with how
   * often the participant stopped talking, drifting two participants apart from each other; and the
   * media clock's rounding accumulated instead of cancelling out. Here a measurement that reads
   * high or low is absorbed by the next one, because both of its terms are absolute.
   *
   * Audio the server never received is silence as far as the timeline is concerned, and shows up in
   * the next measurement as exactly that: a frame the gateway dropped, or one evicted from
   * `pendingFrames`, leaves a hole that is real. The alternative is a timeline shorter than the
   * meeting.
   *
   * @param pcm16 The frame's payload, exactly as it goes on the wire.
   * @param timestampMs Graph's media timestamp for this frame.
   */
  private sendAccounted(pcm16: Uint8Array, timestampMs: number): void {
    const socket = this.socket
    if (socket?.readyState !== OPEN_STATE) return
    if (this.mediaBaseMs === null) {
      this.mediaBaseMs = timestampMs
    } else {
      const accountedMediaMs = this.mediaBaseMs + this.sentAudioMs + this.reportedGapMs
      // Rounded before it is both sent and remembered, so the two sides never disagree by the
      // fraction. What the rounding leaves behind is still owed and turns up in the next one.
      const gapMs = Math.round(timestampMs - accountedMediaMs)
      if (gapMs >= AUDIO_GAP_MIN_MS) {
        socket.send(JSON.stringify({ event: 'audio.gap', ms: gapMs }))
        this.reportedGapMs += gapMs
      }
    }
    socket.send(pcm16)
    this.sentAudioMs += pcm16.byteLength / AUDIO_BYTES_PER_MS
  }

  /** Drop this client's place on the media clock, so the next frame starts a fresh run. */
  private resetMediaAccounting(): void {
    this.mediaBaseMs = null
    this.sentAudioMs = 0
    this.reportedGapMs = 0
  }

  async stop(): Promise<void> {
    if (this.closed) return
    const socket = this.socket
    if (!socket) {
      this.close()
      return
    }
    if (socket.readyState === OPEN_STATE) socket.send(JSON.stringify({ event: 'stop' }))
    await Promise.race([
      new Promise<void>((resolve) => {
        this.closeResolver = resolve
      }),
      new Promise<void>((resolve) => setTimeout(resolve, STOP_TIMEOUT_MS)),
    ])
    this.close()
  }

  close(): void {
    if (this.closed) return
    this.closed = true
    this.ready = false
    this.pendingFrames.length = 0
    const socket = this.socket
    this.socket = null
    socket?.close()
    this.resolveClosed()
  }

  private async open(): Promise<void> {
    try {
      // Alongside the token rather than after it: this runs when a participant
      // first speaks, and a second round trip in series would hold their audio
      // in `pendingFrames` for no reason. Never rejects.
      const [response, backlogUserId] = await Promise.all([
        (this.options.fetchToken ?? fetchSonioxToken)(),
        ensureBacklogUserId(),
      ])
      this.backlogUserId = backlogUserId
      const token = response.token ?? response.apiKey
      if (!token || !response.wsUrl) throw new Error('AI STT token response is invalid')
      if (this.closed) return

      const socket = (this.options.createWebSocket ?? ((url: string) => new WebSocket(url)))(
        tokenizedWebSocketUrl(response.wsUrl, token)
      )
      socket.binaryType = 'arraybuffer'
      this.socket = socket
      socket.onmessage = (message) => this.handleMessage(message.data)
      socket.onerror = () =>
        this.options.onError?.(
          new Error(`AI WebSocket failed for ${this.participant.displayName}`),
          this.participant
        )
      socket.onclose = () => {
        this.ready = false
        this.socket = null
        // Nothing reopens this client today — `connectPromise` is never cleared — so this is for
        // the reconnect that would otherwise inherit a base from before the outage and report the
        // downtime as silence on top of the seed the new socket is already given.
        this.resetMediaAccounting()
        this.resolveClosed()
      }
    } catch (error) {
      this.options.onError?.(
        error instanceof Error ? error : new Error('Could not start participant AI stream'),
        this.participant
      )
      throw error
    }
  }

  private handleMessage(data: unknown): void {
    if (typeof data !== 'string') return
    let event: ProxySttEvent
    try {
      // Forwarded with the service's own `start_time`, deliberately. An earlier attempt shifted it
      // by how much of the meeting had passed when this socket opened, on the theory that the
      // service timed each socket from its own first audio frame. Staging said otherwise: markers
      // in the Input column read past the header's elapsed timer, which is impossible on the
      // meeting's clock, and the panes had been consistent before the shift was added. The service
      // is what owns this timeline — `audio.gap` is how it is told about the silences.
      event = JSON.parse(data) as ProxySttEvent
    } catch {
      return
    }

    if (event.event === 'connected') {
      if (this.socket?.readyState !== OPEN_STATE) return
      const userName = getCurrentUserName()
      this.socket.send(
        JSON.stringify({
          event: 'config',
          mode: 'both',
          // Read now rather than captured: this runs when THIS participant's socket
          // opens, which in native mode is when they first speak.
          meetingElapsedMs: this.options.getMeetingElapsedMs?.(),
          sttProvider: toProxySttProvider(this.options.sttProvider ?? 'soniox'),
          meetingId: this.options.meetingId,
          userId: getCurrentUserId(),
          // The signed-in reader's name, not the Teams participant's. Absent
          // rather than blank, matching the other optional keys on this frame.
          ...(userName ? { userName } : {}),
          // Backlog's own numeric id for the reader's account. Absent rather
          // than null when they have no grant, matching the other optional
          // keys on this frame.
          ...(this.backlogUserId !== null ? { userIdBacklog: this.backlogUserId } : {}),
          // Absent rather than null for a meeting with no project, matching
          // the other optional keys here.
          ...(this.options.projectKey ? { projectKey: this.options.projectKey } : {}),
          // This socket is one participant of a meeting whose other participants have their own
          // sockets. Tells the server to summarize the meeting ONCE across all of them, to honor
          // `audio.gap`, and to attribute this socket's speech to a known person instead of
          // diarizing a stream that only ever holds one speaker.
          nativeMode: true,
          participant: {
            id: this.participant.providerParticipantId,
            displayName: this.participant.displayName,
          },
          audioFormat: 'pcm_s16le',
          sampleRate: 16000,
          numChannels: 1,
          translation: {
            type: this.options.mode ?? 'one_way',
            sourceLang: this.options.sourceLang,
            targetLang: this.targetLang,
          },
          context: normalizeSttConfigContext(this.options.context),
          ...(this.options.getRealtimeFeatures
            ? { features: this.options.getRealtimeFeatures() }
            : {}),
          ...(this.options.getAssistModel
            ? { assistModel: this.options.getAssistModel() }
            : {}),
          ...(this.options.getAssistRoutes
            ? { assistRoutes: this.options.getAssistRoutes() }
            : {}),
        })
      )
      return
    }

    if (event.event === 'assist.routes.status') {
      this.options.onAssistRoutesStatus?.(event)
      return
    }

    if (event.event === 'assist.routes.update.error') {
      this.options.onAssistRoutesError?.(
        (event as { code?: unknown }).code === 'not_supported' ? 'not_supported' : 'bad_request'
      )
      return
    }

    if (event.event === 'assist.model.status') {
      this.options.onAssistModelStatus?.(event)
      return
    }

    if (event.event === 'assist.model.update.error') {
      this.options.onAssistModelError?.(
        (event as { code?: unknown }).code === 'not_supported' ? 'not_supported' : 'bad_request'
      )
      return
    }

    if (event.event === 'ready') {
      this.ready = true
      this.options.onRealtimeFeaturesReady?.(event)
      // Only meaningful on a resumed timeline, and only once reconnect exists —
      // nothing reopens a socket today, so `resumed` is always false in practice.
      // Reported regardless so the caller decides, rather than this layer guessing.
      const seedMs = event.timeline_seed_ms
      if (typeof seedMs === 'number') {
        this.options.onTimelineAnchor?.({
          seedMs,
          resumed: event.timeline_resumed === true,
        })
      }
      // Before this socket's own audio: the meeting's roster is what the server's summarizers
      // attribute from, and it is otherwise assembled one `config.participant` at a time as each
      // person FIRST SPEAKS. Handing over everyone the gateway has already named closes that gap,
      // which is what stopped Confirm Action items from appearing without a name and gaining one
      // several cycles later.
      this.sendSpeakerMap()
      // Through the same accounting as a live frame rather than raw: this audio is part of what the
      // server receives, and leaving it out of the sent total would make the first measurement
      // after it report the whole buffered span as silence.
      for (const frame of this.pendingFrames) this.sendAccounted(frame.pcm16, frame.timestampMs)
      this.pendingFrames.length = 0
      return
    }

    if (event.event === 'features.updated') {
      this.options.onRealtimeFeaturesUpdated?.(event)
      return
    }

    if (event.event === 'features.update.error') {
      this.options.onRealtimeFeaturesError?.(
        event.code === 'not_supported' ? 'not_supported' : 'bad_request'
      )
      return
    }

    if (event.event === 'error') {
      this.options.onError?.(
        new Error(event.message ?? `AI stream failed for ${this.participant.displayName}`),
        this.participant
      )
      return
    }

    // Meeting-level, not participant-level: the server computes one summary for the whole meeting
    // and pushes it down every participant socket, so this is forwarded as-is rather than turned
    // into tokens for this speaker.
    if (isInsightsEvent(event.event)) {
      this.options.onInsights?.(event as ParticipantAiInsightsEvent)
      return
    }

    // A live language change: the server rotates its upstream STT session and this socket keeps
    // running, so this is an acknowledgment to report rather than transcript content. Handled
    // before the token fallthrough below, which would otherwise hand the whole ack to
    // `onEvent`/`proxyEventToTokens`.
    if (event.event === 'language.updated') {
      const ack = event as unknown as { translation?: { targetLang?: string } }
      this.options.onLanguageUpdate?.({
        participant: this.participant,
        ok: true,
        targetLang: ack.translation?.targetLang,
      })
      return
    }

    if (event.event === 'language.update.error') {
      this.options.onLanguageUpdate?.({
        participant: this.participant,
        ok: false,
        message: event.message,
      })
      return
    }

    if (event.event === 'cost.summary') {
      const cost = event as { currency?: string; total_cost_usd?: number }
      if (cost.currency && typeof cost.total_cost_usd === 'number') {
        this.options.onCost?.(this.participant, cost.currency, cost.total_cost_usd)
      }
      return
    }

    const participantEvent = applyTeamsIdentityToEvent(this.participant, event)
    const update = proxyEventToTokens(participantEvent)
    this.displayQueue.push(this.participant, participantEvent, {
      ...update,
      finalTokens: applyTeamsIdentity(this.participant, update.finalTokens),
      partialTokens: applyTeamsIdentity(this.participant, update.partialTokens),
      batchTokens: applyTeamsIdentity(this.participant, update.batchTokens),
    })
  }

  /**
   * Turn realtime panes on or off for this participant's socket.
   *
   * @param features Flags to change. Only boolean keys are sent.
   * @returns False when this socket is not ready or the partial is empty.
   */
  updateFeatures(features: Partial<RealtimeFeatures>): boolean {
    if (!this.ready || this.socket?.readyState !== OPEN_STATE) return false
    const partial = sanitizeFeatureUpdate(features)
    if (!partial) return false
    this.socket.send(JSON.stringify({ event: 'features.update', features: partial }))
    return true
  }

  /**
   * Ask for a different set of routes over this socket.
   *
   * @param routes The routes to work from now on.
   * @returns False when this socket is not ready.
   */
  updateAssistRoutes(routes: AssistRoute[]): boolean {
    if (!this.ready || this.socket?.readyState !== OPEN_STATE) return false
    this.socket.send(JSON.stringify({ event: 'assist.routes.update', routes }))
    return true
  }

  /**
   * Ask for a different assistant over this socket.
   *
   * @param model The assistant to switch to.
   * @returns False when this socket is not ready.
   */
  updateAssistModel(model: AssistModel): boolean {
    if (!this.ready || this.socket?.readyState !== OPEN_STATE) return false
    this.socket.send(JSON.stringify({ event: 'assist.model.update', model }))
    return true
  }

  /** Ask for end-of-meeting minutes over this socket. False when it cannot carry the request. */
  requestMinutes(requestId: string): boolean {
    if (this.closed || this.socket?.readyState !== OPEN_STATE) return false
    this.socket.send(JSON.stringify({ event: 'meeting-minutes', request_id: requestId }))
    return true
  }

  private resolveClosed(): void {
    this.closeResolver?.()
    this.closeResolver = null
  }
}

/** Owns exactly one AI STT WebSocket for each Teams providerSessionId. */
export class ParticipantAiStreamCoordinator {
  private readonly options: ParticipantAiStreamCoordinatorOptions
  private readonly streams = new Map<string, ParticipantAiStreamClient>()
  private readonly displayQueue: ParticipantTranscriptDisplayQueue<
    TeamsAudioParticipant,
    ProxySttEvent,
    ParticipantAiTokenUpdate
  >
  private paused = false
  /** The meeting's roster, kept here so a socket opened LATER still receives it (see setRoster). */
  private roster: Record<string, string> = {}
  /** The meeting's translation language, kept here for the same reason as `roster`. */
  private targetLang: string

  constructor(options: ParticipantAiStreamCoordinatorOptions) {
    this.options = options
    this.targetLang = options.targetLang
    this.displayQueue = new ParticipantTranscriptDisplayQueue((participant, event, update) => {
      options.onEvent?.(participant, event)
      if (
        update.finalTokens.length > 0 ||
        update.partialTokens.length > 0 ||
        update.batchTokens.length > 0 ||
        update.clearPartial
      ) {
        options.onTokens(participant, update)
      }
    })
  }

  /**
   * Publish the meeting's `providerParticipantId -> displayName` roster to every socket.
   *
   * Call it whenever the participant list changes: a person the gateway names at minute 0 but who
   * only speaks at minute 4 is otherwise unknown to the summarizers for those four minutes, and
   * anything they said through an unresolved media source stays unattributable for the rest of the
   * meeting. Idempotent — the server merges by id and re-sending the same map changes nothing.
   */
  setRoster(roster: Record<string, string>): void {
    this.roster = roster
    for (const stream of this.streams.values()) stream.setRoster(roster)
  }

  /**
   * Change the language the whole meeting is translated into, mid-session (BRAI-359).
   *
   * Remembered as well as broadcast, for the same reason as `setRoster`: Teams only streams
   * unmixed audio for active speakers, so a participant who has not spoken yet has no socket to
   * tell — and the socket they open later must not go back to the language the meeting started on.
   *
   * @param targetLang Language code to translate into from here on.
   * @returns How many open sockets were told, i.e. how many acknowledgments to expect. `0` is a
   *   normal outcome (nobody has spoken yet) and still means the change took effect.
   */
  setTargetLang(targetLang: string): number {
    this.targetLang = targetLang
    let notified = 0
    for (const stream of this.streams.values()) {
      if (stream.setTargetLang(targetLang)) notified += 1
    }
    return notified
  }

  /**
   * Pause or resume every participant socket at once.
   *
   * The flag is remembered rather than only broadcast: a participant who starts speaking during
   * the pause would otherwise open a stream that never heard about it. While paused no audio is
   * accepted at all, so a paused meeting bills nothing.
   */
  setPaused(paused: boolean): void {
    this.paused = paused
    for (const stream of this.streams.values()) stream.setPaused(paused)
  }

  async writeAudio(frame: TeamsAudioFrame): Promise<void> {
    if (this.paused) return
    let stream = this.streams.get(frame.participant.providerSessionId)
    if (!stream) {
      stream = new ParticipantAiStreamClient(frame.participant, this.options, this.displayQueue)
      stream.setRoster(this.roster)
      // Not `this.options.targetLang`: that is the language the meeting STARTED on, and this socket
      // may be opening after the user already switched (its `config` has not been sent yet).
      stream.setTargetLang(this.targetLang)
      this.streams.set(frame.participant.providerSessionId, stream)
    }
    stream.writeAudio(frame)
    try {
      await stream.connect()
    } catch (error) {
      if (this.streams.get(frame.participant.providerSessionId) === stream) {
        this.streams.delete(frame.participant.providerSessionId)
      }
      stream.close()
      throw error
    }
  }

  /**
   * Ask the meeting for its end-of-meeting minutes, over ANY one of the participant sockets.
   *
   * One request, not one per participant: the minutes are computed from the meeting's merged
   * transcript, so asking every socket would run the same expensive job N times. The first socket
   * that can carry the request wins; the result arrives on `onInsights` (from whichever socket the
   * server broadcasts it to, which need not be this one).
   */
  requestMinutes(requestId: string): boolean {
    for (const stream of this.streams.values()) {
      if (stream.requestMinutes(requestId)) return true
    }
    return false
  }

  /**
   * Apply one pane change to every participant socket this screen has open.
   *
   * Each socket keeps its own switches. This client's panes are one view, so
   * every socket it opened has to be told or a speaker who connects later
   * would keep filling a pane the reader already folded.
   *
   * @param features Flags to change.
   * @returns True when at least one socket accepted the frame.
   */
  updateFeatures(features: Partial<RealtimeFeatures>): boolean {
    let sent = false
    for (const stream of this.streams.values()) {
      if (stream.updateFeatures(features)) sent = true
    }
    return sent
  }

  /**
   * Ask every participant socket for the same assistant.
   *
   * Each socket carries its own, and the reader has one Copilot panel: a
   * speaker whose socket was not told would go on being answered by the model
   * the panel stopped showing.
   *
   * @param model The assistant to switch to.
   * @returns True when at least one socket accepted the frame.
   */
  updateAssistModel(model: AssistModel): boolean {
    let sent = false
    for (const stream of this.streams.values()) {
      if (stream.updateAssistModel(model)) sent = true
    }
    return sent
  }

  /**
   * Ask every participant socket for the same routes, for the reason the
   * assistant choice goes to all of them: one panel, many sockets feeding it.
   *
   * @param routes The routes to work from now on.
   * @returns True when at least one socket accepted the frame.
   */
  updateAssistRoutes(routes: AssistRoute[]): boolean {
    let sent = false
    for (const stream of this.streams.values()) {
      if (stream.updateAssistRoutes(routes)) sent = true
    }
    return sent
  }

  async stop(): Promise<void> {
    const streams = [...this.streams.values()]
    this.streams.clear()
    try {
      await Promise.all(streams.map((stream) => stream.stop()))
    } finally {
      this.displayQueue.reset()
    }
  }

  close(): void {
    for (const stream of this.streams.values()) stream.close()
    this.streams.clear()
    this.displayQueue.reset()
  }
}
